import React, { useState } from 'react';
import { Card, Descriptions, Tag, Button, Space, Modal, Form, Input, InputNumber, Select, DatePicker, message, Spin, Row, Col } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;

const OnlineStudentDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [paymentForm] = Form.useForm();

    // Fetch student details
    const { data: student, isLoading } = useQuery(['online-student', id], async () => {
        const response = await api.get(`/online-students/${id}`);
        return response.data.data;
    });

    // Update student mutation
    const updateMutation = useMutation(
        (values) => api.put(`/online-students/${id}`, values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.studentUpdated'));
                setIsEditModalVisible(false);
                queryClient.invalidateQueries(['online-student', id]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    // Add payment mutation
    const addPaymentMutation = useMutation(
        (values) => api.post(`/online-students/${id}/payment`, values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.paymentAdded'));
                setIsPaymentModalVisible(false);
                paymentForm.resetFields();
                queryClient.invalidateQueries(['online-student', id]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    const handleEdit = () => {
        form.setFieldsValue({
            ...student,
            dateOfBirth: student.dateOfBirth ? dayjs(student.dateOfBirth) : null,
            enrollmentDate: student.enrollmentDate ? dayjs(student.enrollmentDate) : null,
        });
        setIsEditModalVisible(true);
    };

    const handleUpdate = (values) => {
        const formattedValues = {
            ...values,
            dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
            enrollmentDate: values.enrollmentDate ? values.enrollmentDate.toISOString() : null,
        };
        updateMutation.mutate(formattedValues);
    };

    const handleAddPayment = (values) => {
        addPaymentMutation.mutate(values);
    };

    const getPaymentStatusTag = () => {
        const { totalFees, paidAmount } = student?.paymentDetails || {};
        if (paidAmount >= totalFees) {
            return <Tag color="green">{t('onlineAccounting.paid')}</Tag>;
        } else if (paidAmount > 0) {
            return <Tag color="orange">{t('onlineAccounting.partial')}</Tag>;
        }
        return <Tag color="red">{t('onlineAccounting.unpaid')}</Tag>;
    };

    const getStatusTag = (status) => {
        const color = status === 'active' ? 'green' : status === 'inactive' ? 'orange' : 'red';
        return <Tag color={color}>{t(`onlineAccounting.${status}`)}</Tag>;
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!student) {
        return <div>{t('onlineAccounting.studentNotFound')}</div>;
    }

    const { totalFees, paidAmount, remainingBalance, currency } = student.paymentDetails || {};
    const currencySymbol = currency === 'IQD' ? 'IQD' : '$';

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/online-students')}>
                    {t('back')}
                </Button>
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                    {t('edit')}
                </Button>
            </Space>

            <Row gutter={16}>
                <Col span={16}>
                    <Card title={t('onlineAccounting.studentDetails')} style={{ marginBottom: 16 }}>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label={t('onlineAccounting.studentId')}>
                                {student.studentId}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.status')}>
                                {getStatusTag(student.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.firstName')}>
                                {student.firstName}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.lastName')}>
                                {student.lastName}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.email')}>
                                {student.email}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.phone')}>
                                {student.phone}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.dateOfBirth')}>
                                {student.dateOfBirth ? dayjs(student.dateOfBirth).format('YYYY-MM-DD') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.gender')}>
                                {student.gender ? t(`onlineAccounting.${student.gender}`) : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.courseName')} span={2}>
                                {student.courseName}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('enrollmentDate')} span={2}>
                                {dayjs(student.enrollmentDate).format('YYYY-MM-DD HH:mm')}
                            </Descriptions.Item>
                            {student.notes && (
                                <Descriptions.Item label={t('onlineAccounting.notes')} span={2}>
                                    {student.notes}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </Col>

                <Col span={8}>
                    <Card title={t('onlineAccounting.paymentDetails')} style={{ marginBottom: 16 }}>
                        <Descriptions bordered column={1}>
                            <Descriptions.Item label={t('onlineAccounting.totalFees')}>
                                <strong>{currencySymbol}{totalFees?.toLocaleString() || '0'}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.paidAmount')}>
                                <strong style={{ color: '#52c41a' }}>
                                    {currencySymbol}{paidAmount?.toLocaleString() || '0'}
                                </strong>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.remaining')}>
                                <strong style={{ color: remainingBalance > 0 ? '#ff4d4f' : '#52c41a' }}>
                                    {currencySymbol}{remainingBalance?.toLocaleString() || '0'}
                                </strong>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.paymentStatus')}>
                                {getPaymentStatusTag()}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('onlineAccounting.currency')}>
                                {currency}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
            </Row>

            {/* Edit Modal */}
            <Modal
                title={t('onlineAccounting.editStudent')}
                open={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleUpdate}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="status" label={t('onlineAccounting.status')} initialValue="active">
                                <Select>
                                    <Option value="active">{t('onlineAccounting.active')}</Option>
                                    <Option value="inactive">{t('onlineAccounting.inactive')}</Option>
                                    <Option value="suspended">{t('onlineAccounting.suspended')}</Option>
                                    <Option value="graduated">{t('onlineAccounting.graduated')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label={t('onlineAccounting.firstName')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label={t('onlineAccounting.lastName')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label={t('onlineAccounting.email')} rules={[{ required: true, type: 'email' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label={t('onlineAccounting.phone')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dateOfBirth" label={t('onlineAccounting.dateOfBirth')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="gender" label={t('onlineAccounting.gender')}>
                                <Select>
                                    <Option value="male">{t('onlineAccounting.male')}</Option>
                                    <Option value="female">{t('onlineAccounting.female')}</Option>
                                    <Option value="other">{t('onlineAccounting.other')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="courseName" label={t('onlineAccounting.course')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name={['paymentDetails', 'totalFees']} label={t('onlineAccounting.totalFees')} rules={[{ required: true }]}>
                                <Input type="number" min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name={['paymentDetails', 'currency']} label={t('onlineAccounting.currency')} initialValue="USD">
                                <Select>
                                    <Option value="USD">USD</Option>
                                    <Option value="IQD">IQD</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label={t('onlineAccounting.notes')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Space>
                        <Button type="primary" htmlType="submit" loading={updateMutation.isLoading}>
                            {t('update')}
                        </Button>
                        <Button onClick={() => {
                            setIsEditModalVisible(false);
                            form.resetFields();
                        }}>
                            {t('cancel')}
                        </Button>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default OnlineStudentDetail;
